import { useMutation } from "@apollo/client";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

import { IdealWorkType } from "../../../utils/constant";

import "../candidate.scss";
import { EDIT_EMPLOYMENT_HISTORY } from "../gql";

import EDIT_ICON from "../../../assets/images/edit-icon-menu.svg";
import CustomButton from "../../../components/layout/CustomButton";
import { AppContext } from "../../../context";
import { customSelectDropDown } from "../../../components/styled/customSelect";

const EditEmploymentHistory = ({ item, updateUI, candidateId }: any) => {
  const { theme } = useContext(AppContext);
  const [editModal, setEditModal] = useState(false);

  const convertDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // console.log(companyName)
  const [editEmployment, { loading }] = useMutation(EDIT_EMPLOYMENT_HISTORY);

  const editEmpHistory = async (values) => {
    values.workType = values?.workType?.value;
    values.id = item?.id;
    values.candidateId = candidateId;

    if (values.startedAt) {
      const [year, month, day] = values.startedAt.split('-').map(Number);
      const startDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      values.startedAt = startDate.toISOString();
    }

    if (values.endedAt && !values.currentlyWorking) {
      const [year, month, day] = values.endedAt.split('-').map(Number);
      const endDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      values.endedAt = endDate.toISOString();
    } else if (values.currentlyWorking) {
      values.endedAt = null;
    }

    if (!values.currentlyWorking && !values.endedAt) {
      toast.error("End date is required");
      return;
    }

    const res = await editEmployment({ variables: { ...values } });
    if (res?.data) {
      toast.success("Successfully Edited!");
      updateUI();
      setEditModal(!editModal);
    } else if (res?.errors) {
      toast.error(res.errors[0]?.message);
    }
  };

  const { handleSubmit, control, watch, setValue, reset, formState: { errors } } = useForm<any>({
    defaultValues: { companyName: '', jobTitle: '', workType: null, startedAt: '', endedAt: '', currentlyWorking: false, description: '' },
  });
  const currentlyWorking = watch('currentlyWorking');

  useEffect(() => {
    if (editModal && item) {
      reset({
        companyName: item?.companyName ?? '',
        jobTitle: item?.jobTitle ?? '',
        workType: IdealWorkType?.find((val) => val.value === item?.workType) ?? null,
        startedAt: convertDate(item?.startedAt) ?? '',
        endedAt: item?.endedAt ? convertDate(item?.endedAt) : '',
        currentlyWorking: item?.endedAt === null,
        description: item?.description ?? '',
      });
    }
  }, [editModal, item, reset]);

  return (
    <div className="mr-2">
      <button
        className="iconButtonAction"
        onClick={() => setEditModal(!editModal)}
      >
        <img src={EDIT_ICON} alt="edit" />
      </button>

      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(!editModal)}
        className="revampDialog revampDialogWidth"
      >
        <ModalHeader toggle={() => setEditModal(!editModal)}>
          Employment History
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col lg="6" md="6" sm="12" xs="12">
                <FormGroup>
                  <Label>Company Name</Label>
                  <Controller
                    name="companyName"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: "Company name is required!",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        type="text"
                        placeholder="Company Name"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        innerRef={field.ref}
                      />
                    )}
                  />
                  <small className="text-danger">
                    {errors?.companyName?.message as any}
                  </small>
                </FormGroup>
              </Col>
              <Col lg="6" md="6" sm="12" xs="12">
                <FormGroup>
                  <Label>Job Title</Label>
                  <Controller
                    name="jobTitle"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="text"
                        placeholder="Job Title"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        innerRef={field.ref}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col lg="6" md="6" sm="12" xs="12">
                <FormGroup>
                  <Label>Work Type</Label>
                  <Controller
                    name="workType"
                    control={control}
                    rules={{ required: "Please select work type!" }}
                    render={({ field }) => (
                      <Select
                        value={field.value ?? null}
                        onChange={(option) => field.onChange(option)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        options={IdealWorkType}
                        placeholder="Work Type"
                        styles={{ ...customSelectDropDown(theme) }}
                      />
                    )}
                  />
                  <small className="text-danger">
                    {errors?.workType?.message as any}
                  </small>
                </FormGroup>
              </Col>
              <Col lg="6" md="6" sm="12" xs="12">
                <FormGroup>
                  <Label>Start Date</Label>
                  <Controller
                    name="startedAt"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: "Start date is required!",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        type="date"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        innerRef={field.ref}
                      />
                    )}
                  />
                  <small className="text-danger">
                    {errors?.startedAt?.message as any}
                  </small>
                </FormGroup>
              </Col>
              <Col lg="6" md="6" sm="12" xs="12">
                <FormGroup>
                  <Label>End Date</Label>
                  <Controller
                    name="endedAt"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="date"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        innerRef={field.ref}
                        disabled={currentlyWorking}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col lg="6" md="6" sm="12" xs="12">
                <FormGroup className="d-flex align-items-center pt-4">
                  <Controller
                    name="currentlyWorking"
                    control={control}
                    render={({ field }) => (
                      <Input
                        className="mb-1 mr-2"
                        style={{ position: "static", marginLeft: "0.1rem" }}
                        type="checkbox"
                        checked={field.value === true}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          field.onChange(isChecked);
                          if (isChecked) setValue('endedAt', '');
                        }}
                        onBlur={field.onBlur}
                        innerRef={field.ref}
                      />
                    )}
                  />
                  <Label>Currently Working</Label>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Description</Label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <textarea
                    className="form-control"
                    rows={4}
                    id="description"
                    placeholder="No description Found"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                )}
              />
            </FormGroup>
            <div className="d-flex align-items-center justify-content-end pt-2 pb-2">
              <button
                onClick={() => setEditModal(!editModal)}
                className="buttonPrimary mr-3"
                type="button"
              >
                Cancel
              </button>

              <CustomButton
                buttonText="Edit"
                buttonColor="primary"
                buttonType="submit"
                className="big-width"
                buttonClick={handleSubmit(editEmpHistory)}
                loading={loading}
              />
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default EditEmploymentHistory;
